import { useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import type { UIElement } from '@adyen/adyen-web';
import type { AdyenApplePayHandlers } from '@/types/adyen';
import { CheckoutContext } from '@/context/CheckoutContext';
import { resolveCheckoutGameName } from '@/utils/publisher';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { AdyenApplePayPaymentMethod } from '@/types/adyen';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { Errors } from '@/constants/errors';
import { logError } from '@/utils/logger';
import {
  installApplePayMerchantValidationProbe,
  resetApplePayCancelDiagnostics,
} from '@/utils/applePayCancelDiagnostics';
import { useAdyenExpressError } from '@/hooks/useAdyenExpressError';
import { useAdyenApplePayBillingZipTax } from '@/hooks/useAdyenApplePayBillingZipTax';
import { trackApplePaySlowLoad, type ApplePayLoadPhases } from '@/utils/conversionTracking';
import { notifyPaymentIntentClick } from '@/utils/paymentClickEvents';
import { formatAdyenWalletAvailabilityReason, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';

export interface UseAdyenApplePayOptions extends ExpressCheckoutComponentProps {
  onClick?: AdyenApplePayHandlers['onClick'];
}

export function useAdyenApplePay({ onReady, onClick }: UseAdyenApplePayOptions = {}) {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodsSettings,
    pricing,
    customer,
    featureFlags,
    sessionStartedWithoutValidTax,
    publisher,
    boot,
    actions: { runPayment },
  } = context;
  // ACDEV-26742: display-only company-name label for the Apple Pay sheet's
  // grand-total row (gated by APPLE_PAY_COMPANY_NAME_LABEL inside
  // `getApplePayTotalLabel`). Never leaves the device — the bank statement is
  // owned by the BE `shopperStatement` / Adyen transaction-description config.
  const companyName = resolveCheckoutGameName(boot?.gameName, publisher?.companyName || boot?.companyName);
  // Read inside the async init and the SDK handlers so slow-load labels and the
  // checkout-email fallback reflect the latest context without widening the
  // effect's dependency array (which would re-init Adyen mid-sheet).
  const contextRef = useRef(context);
  contextRef.current = context;

  const [applePayElement, setApplePayElement] = useState<UIElement | null>(null);
  const [error, setErrorState] = useState<Error | null>(null);
  const isPaymentInProgress = useRef(false);
  const isPaySessionStartingRef = useRef(false);
  const applePaymentDetailsRef = useRef<{ email: string; paymentMethodDetails: Record<string, unknown> } | null>(null);

  // ACDEV-25067: recompute tax from the wallet ZIP (sourced from the card's
  // redacted billing contact) and patch the open Apple Pay sheet total. Read
  // through a ref so the (pricing-dependent) callback identity never
  // re-initializes the component mid-sheet — the effect below keys on
  // `pricing` only.
  const { onPaymentMethodSelected, capturedWalletTaxRef, applyWalletTaxToCheckout } = useAdyenApplePayBillingZipTax();
  const onPaymentMethodSelectedRef = useRef(onPaymentMethodSelected);
  onPaymentMethodSelectedRef.current = onPaymentMethodSelected;
  // See `useAdyenGooglePay`: keep the SDK handler closure stable while still
  // calling the latest `applyWalletTaxToCheckout` on cancel/failure.
  const applyWalletTaxToCheckoutRef = useRef(applyWalletTaxToCheckout);
  applyWalletTaxToCheckoutRef.current = applyWalletTaxToCheckout;

  const applePayPaymentMethod = useMemo(
    () =>
      paymentMethodsSettings.find(
        (pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY && pm.provider === ProviderEnum.ADYEN
      ) as AdyenApplePayPaymentMethod | undefined,
    [paymentMethodsSettings]
  );

  const { handleError, handleCancel } = useAdyenExpressError({
    paymentMethod: PaymentMethodEnum.APPLE_PAY,
    isPaymentInProgress,
    isPaySessionStartingRef,
  });

  useEffect(() => {
    if (!applePayPaymentMethod) {
      onReady?.(false, WALLET_AVAILABILITY_REASON.METHOD_NOT_OFFERED);
      return;
    }
    let applePayInstance: UIElement | null = null;

    const initApplePay = async () => {
      // Phase stopwatch: attribute a >5s stuck load to init / component
      // construction / availability probe (see `trackApplePaySlowLoad`).
      const startedAt = Date.now();
      const phases: ApplePayLoadPhases = {};
      try {
        // ACDEV-25945: wrap completeMerchantValidation so a later CANCEL can be
        // classified (domain failure vs PassKit rejection vs real user cancel).
        installApplePayMerchantValidationProbe();

        await adyenApplePayService.initializeCheckoutInstance(applePayPaymentMethod, customer ?? undefined);
        phases.sdk_init = Date.now() - startedAt;

        const guardedOnClick: AdyenApplePayHandlers['onClick'] = (resolve, reject) => {
          if (isPaySessionStartingRef.current) {
            resolve();
            return;
          }
          isPaySessionStartingRef.current = true;
          // Fresh session: drop any snapshot from a previous sheet.
          resetApplePayCancelDiagnostics();
          if (onClick) {
            onClick(resolve, reject);
          } else {
            resolve();
          }
        };

        // ACDEV-25067: pair the callback's presence with `useAdyenApplePayBillingZipTax`'s
        // US/CA gate. When the hook returned `undefined` (non-US/CA session),
        // we hand Adyen NO `onPaymentMethodSelected` at all — matching
        // Stripe ECE's `onShippingAddressChange: ... : undefined` pattern.
        const hasPaymentMethodCallback = !!onPaymentMethodSelectedRef.current;

        const handlers: AdyenApplePayHandlers = {
          onClick: guardedOnClick,
          // ACDEV-25067: fires before authorization with the selected card's
          // redacted billing contact (postal code) — the pre-auth hook that
          // now carries the ZIP (previously we listened on the shipping side).
          // Also fires on sheet open for the default card. The stable wrapper
          // defers to the latest pricing-aware callback without re-creating
          // the component.
          ...(hasPaymentMethodCallback
            ? {
                onPaymentMethodSelected: (resolve, reject, event) =>
                  onPaymentMethodSelectedRef.current!(resolve, reject, event),
              }
            : {}),
          onAuthorized: (data, actions) => {
            const applePayPayment = data.authorizedEvent.payment;
            // `shippingContact` is the only contact Apple populates with an
            // email (see `getAdyenApplePayConfig`); `billingContact` is read
            // defensively but never carries one. The checkout-email fallback
            // must come off `contextRef`: this closure is created once per
            // `pricing`, so the destructured `customer` predates anything the
            // shopper typed into the email field after mount.
            applePaymentDetailsRef.current = {
              email:
                applePayPayment.shippingContact?.emailAddress ||
                applePayPayment.billingContact?.emailAddress ||
                contextRef.current.customer?.email ||
                '',
              paymentMethodDetails: applePayPayment.token.paymentMethod as unknown as Record<string, unknown>,
            };
            // Payment is authorized; a later sheet-close CANCEL must not reuse the
            // merchant-validation timing snapshot (ACDEV-25945 false positives).
            resetApplePayCancelDiagnostics();
            actions.resolve();
          },
          // ACDEV-25945: the Apple Pay failure log now lives in the shared
          // `handleError` (useAdyenExpressError) so it's emitted in every case,
          // including the silent-cancel path that returns early there.
          onError: (err) => {
            applyWalletTaxToCheckoutRef.current();
            handleError(err);
          },
          // ACDEV-24269: Adyen invokes `onPaymentFailed` after we call
          // `actions.reject()` (via `completeApplePayPayment(false)`) on a
          // server-reported failure or a thrown `runPayment`. Both of those
          // paths already fire `notifyPaymentIntentFailed` via the central
          // `handlePaymentResults` / `runPayment.catch` in CheckoutContent.
          // Firing it again here would duplicate the FE event — just clean
          // up local state.
          onPaymentFailed: () => {
            applyWalletTaxToCheckoutRef.current();
            handleCancel();
          },
          onSubmit: async (state, _, actions) => {
            if (isPaymentInProgress.current) return;
            isPaymentInProgress.current = true;
            adyenApplePayService.setApplePayActions(actions);

            try {
              const details = applePaymentDetailsRef.current;
              if (!details) {
                // Match every other error/failure path in this handler (and
                // the sibling Google Pay / Stripe handlers): flush or restore
                // the wallet-derived tax before bailing. Otherwise
                // `capturedWalletTaxRef` retains a stale result that could be
                // re-flushed by a later cancel, and any `default`-source
                // dirtying of the BE session during the sheet flow never gets
                // re-synced.
                applyWalletTaxToCheckoutRef.current();
                handleError();
                return;
              }

              notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
              await runPayment<AdyenFinalizePaymentResponse>(
                {
                  paymentMethod: state.data.paymentMethod,
                  paymentMethodDetails: details.paymentMethodDetails,
                  email: details.email || contextRef.current.customer?.email,
                  zipCode: capturedWalletTaxRef.current?.zipCode,
                },
                PaymentMethodEnum.APPLE_PAY,
                ProviderEnum.ADYEN
              );
            } catch {
              // ACDEV-24269: `runPayment` swallows its own errors and fires
              // `notifyPaymentIntentFailed` from its own catch, so we must
              // not fire it again here. Just reject so the Apple Pay sheet
              // closes if we ever land here.
              applyWalletTaxToCheckoutRef.current();
              adyenApplePayService.completeApplePayPayment(false);
            } finally {
              isPaymentInProgress.current = false;
              isPaySessionStartingRef.current = false;
            }
          },
        };

        const beforeCreate = Date.now();
        const component = await adyenApplePayService.createApplePayComponent({
          applePayPaymentMethod,
          customer,
          pricing,
          handlers,
          featureFlags,
          sessionStartedWithoutValidTax,
          companyName,
        });
        phases.create_component = Date.now() - beforeCreate;

        if (!component) return;

        applePayInstance = component;
        setApplePayElement(applePayInstance);
        const beforeAvailable = Date.now();
        await component.isAvailable();
        phases.is_available = Date.now() - beforeAvailable;
        trackApplePaySlowLoad(contextRef.current, {
          payment_provider: ProviderEnum.ADYEN,
          phases,
          total_ms: Date.now() - startedAt,
          outcome: 'ready',
        });
        onReady?.(true);
      } catch (err) {
        // A slow failure (incl. isAvailable() rejecting after a long hang) is
        // still worth explaining — phases captured so far pinpoint where it stuck.
        trackApplePaySlowLoad(contextRef.current, {
          payment_provider: ProviderEnum.ADYEN,
          phases,
          total_ms: Date.now() - startedAt,
          outcome: 'error',
        });
        logError('Adyen Apple Pay initialization error', err);
        setErrorState(err instanceof Error ? err : new Error(Errors.payment.failedToInitializeApplePay));
        onReady?.(false, formatAdyenWalletAvailabilityReason(err));
      }
    };

    initApplePay();

    return () => {
      applePayInstance?.unmount();
    };
  }, [pricing]);

  return {
    applePayElement,
    error,
  };
}
